import { axiosClient } from "@/shared/api/axios-client";
import type {
  Prorrateo,
  LineaManualInput,
  SubLineaUpdate,
} from "@/features/prorrateo/types";

/**
 * Capa pura sobre los endpoints del prorrateo de la API.
 *
 * Diseño:
 *   · El wizard de CFDI único llama sin `numeroCfdi` · el backend asume
 *     el slot 1 por default.
 *   · La pantalla `/prorrateo-multiple` pasa `numeroCfdi` explícito por
 *     cada CFDI viable del ZIP.
 *
 * El helper `qcfdi` omite el query param cuando es 1 o undefined para
 * mantener las URLs limpias en el caso del wizard.
 */
const base = (id: string) => `/api/solicitudes/${id}/prorrateo`;

const qcfdi = (numeroCfdi?: number) =>
  numeroCfdi && numeroCfdi !== 1 ? `?numeroCfdi=${numeroCfdi}` : "";

export const prorrateoApi = {
  obtener: (solicitudId: string) =>
    axiosClient.get<Prorrateo>(base(solicitudId)).then((r) => r.data),

  /**
   * Listado de TODOS los prorrateos de la solicitud · un Prorrateo por
   * CFDI viable. Solo aplica al subtipo "Múltiples CFDI".
   */
  listarTodos: (solicitudId: string) =>
    axiosClient
      .get<Prorrateo[]>(`${base(solicitudId)}/todos`)
      .then((r) => r.data),

  confirmar: (solicitudId: string, numeroCfdi?: number) =>
    axiosClient
      .post<Prorrateo>(`${base(solicitudId)}/confirmar${qcfdi(numeroCfdi)}`)
      .then((r) => r.data),

  reabrir: (solicitudId: string, numeroCfdi?: number) =>
    axiosClient
      .post<Prorrateo>(
        `${base(solicitudId)}/reabrir?numeroCfdi=${numeroCfdi ?? 1}`
      )
      .then((r) => r.data),

  agregarLineaManual: (
    solicitudId: string,
    input: LineaManualInput,
    numeroCfdi?: number
  ) =>
    axiosClient
      .post<Prorrateo>(
        `${base(solicitudId)}/lineas/manual${qcfdi(numeroCfdi)}`,
        input
      )
      .then((r) => r.data),

  /** Edita una SUB-LÍNEA de concepto (CeCo, cuenta, importe, indicador). */
  editarSubLinea: (
    solicitudId: string,
    lineaId: string,
    update: SubLineaUpdate,
    numeroCfdi?: number
  ) =>
    axiosClient
      .patch<Prorrateo>(
        `${base(solicitudId)}/lineas/${lineaId}/automatica${qcfdi(numeroCfdi)}`,
        update
      )
      .then((r) => r.data),

  /** Divide un concepto: agrega una sub-línea que inicia en 0 (hereda CeCo/cuenta/indicador). */
  dividirConcepto: (solicitudId: string, conceptoNumero: number, numeroCfdi?: number) =>
    axiosClient
      .post<Prorrateo>(
        `${base(solicitudId)}/conceptos/${conceptoNumero}/dividir${qcfdi(numeroCfdi)}`
      )
      .then((r) => r.data),

  editarLineaManual: (
    solicitudId: string,
    lineaId: string,
    input: LineaManualInput,
    numeroCfdi?: number
  ) =>
    axiosClient
      .patch<Prorrateo>(
        `${base(solicitudId)}/lineas/${lineaId}/manual${qcfdi(numeroCfdi)}`,
        input
      )
      .then((r) => r.data),

  eliminarLinea: (solicitudId: string, lineaId: string, numeroCfdi?: number) =>
    axiosClient
      .delete<Prorrateo>(
        `${base(solicitudId)}/lineas/${lineaId}${qcfdi(numeroCfdi)}`
      )
      .then((r) => r.data),

  /**
   * Re-deriva la cuenta de las líneas automáticas que quedaron "Sin cuenta"
   * (su clave no tenía mapeo al construir el prorrateo). Se llama al abrir el
   * prorrateo · idempotente (si no hay nada que sanar, devuelve el mismo estado).
   */
  rederivarCuentas: (solicitudId: string, numeroCfdi?: number) =>
    axiosClient
      .post<Prorrateo>(`${base(solicitudId)}/rederivar-cuentas${qcfdi(numeroCfdi)}`)
      .then((r) => r.data),

  /**
   * Carga la dispersión de CeCos desde una plantilla Excel (paridad legado ·
   * FB60 y Contrato Anualizado). El navegador ya parseó el xlsx a `filas`
   * (centro de costo por código + importe); el backend reemplaza las líneas
   * del prorrateo con el desglose. La suma se valida al confirmar.
   */
  importarDispersion: (
    solicitudId: string,
    filas: FilaDispersionInput[],
    numeroCfdi?: number
  ) =>
    axiosClient
      .post<Prorrateo>(
        `${base(solicitudId)}/importar-dispersion${qcfdi(numeroCfdi)}`,
        { filas }
      )
      .then((r) => r.data),
};

/**
 * Un renglón de la plantilla de dispersión: concepto (por número · 0 si el prorrateo
 * tiene un solo concepto), CeCo por código, cuenta contable por código (opcional · hereda
 * la del concepto) e importe.
 */
export interface FilaDispersionInput {
  conceptoNumero: number;
  centroDeCostoCodigo: string;
  cuentaContableCodigo?: string;
  importe: number;
}
